/**
 * Utility functions for TreatmentOption <-> CTPO lookups
 *
 * The treatmentOption → CTPO mapping is duplicated and must be kept in sync with LW API,
 * so this could potentially be replaced by fetching the data from LW API at runtime.
 *
 * This is primarily used for determining the correct program taxonomy to use for a
 * given treatment option.
 *
 * CTPO array order = priority. First entry in the list is highest priority CTPO.
 *
 * ---
 *
 * The inverse CTPO → treatmentOption uses first treatmentOption listed in the mapping
 * to resolve ambiguous CTPOs (e.g. Individual/Therapy/DirectAccess/Default appears under
 * INDIVIDUAL_THERAPY before INDIVIDUAL_THERAPY_CHILD). treatmentOption key order = priority.
 *
 * This is not expected to be a common case; verify these cases in the test snapshots.
 *
 */

import { ProgramTaxonomyType } from './types'
import { ProviderProgramTaxonomy } from '../../models/providers/Providers'
import { DEFAULT_PROGRAM_TAXONOMY } from '../common/constants/customerProgram'
import { TREATMENT_OPTIONS, TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'

const TREATMENT_OPTION_TO_CTPOS: Partial<Record<TREATMENT_OPTIONS_TYPE, ProgramTaxonomyType[]>> = {
  [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY]: [
    { clientele: 'Individual', treatment: 'Therapy', partner: 'BlendedCare', offering: 'DBT' },
    { clientele: 'Individual', treatment: 'Therapy', partner: 'BlendedCare', offering: 'Default' },
    { clientele: 'Individual', treatment: 'Therapy', partner: 'BlendedCare', offering: 'Fellowship' },
    { clientele: 'Individual', treatment: 'Therapy', partner: 'DirectAccess', offering: 'Default' },
    { clientele: 'Individual', treatment: 'Therapy', partner: 'Icas', offering: 'Default' },
  ],
  [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD]: [
    { clientele: 'Individual', treatment: 'Therapy', partner: 'DirectAccess', offering: 'Default' },
  ],
  [TREATMENT_OPTIONS.COUPLES_THERAPY]: [
    { clientele: 'Couples', treatment: 'Therapy', partner: 'DirectAccess', offering: 'Default' },
  ],
  [TREATMENT_OPTIONS.FAMILY_THERAPY]: [
    { clientele: 'Family', treatment: 'Therapy', partner: 'DirectAccess', offering: 'Default' },
  ],
  [TREATMENT_OPTIONS.BLENDED_CARE_MEDS]: [
    { clientele: 'Individual', treatment: 'MedicationManagement', partner: 'BlendedCare', offering: 'Default' },
  ],
  [TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS]: [
    { clientele: 'Individual', treatment: 'MedicationManagement', partner: 'DirectAccess', offering: 'Default' },
  ],
  [TREATMENT_OPTIONS.COACHING]: [
    { clientele: 'Individual', treatment: 'Coaching', partner: 'BlendedCare', offering: 'Default' },
  ],
  [TREATMENT_OPTIONS.COACHING_FOR_PARENTS]: [
    { clientele: 'Individual', treatment: 'Coaching', partner: 'BlendedCare', offering: 'Parenting' },
  ],
  [TREATMENT_OPTIONS.PERFORMANCE_COACHING]: [
    { clientele: 'Individual', treatment: 'Coaching', partner: 'BlendedCare', offering: 'Performance' },
  ],
  [TREATMENT_OPTIONS.GUIDED_SELF_CARE]: [
    { clientele: 'Individual', treatment: 'Coaching', partner: 'BlendedCare', offering: 'SingleSession' },
  ],
  [TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER]: [
    { clientele: 'Individual', treatment: 'Therapy', partner: 'BlendedCare', offering: 'AlcoholUseDisorder' },
  ],
  [TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING]: [
    { clientele: 'Individual', treatment: 'Coaching', partner: 'BlendedCare', offering: 'NeurodiversitySkills' },
  ],
  [TREATMENT_OPTIONS.CAREGIVER_TRAINING_FOR_NEURO]: [
    { clientele: 'Individual', treatment: 'Coaching', partner: 'BlendedCare', offering: 'CaregiverTrainingForNeuro' },
  ],
  [TREATMENT_OPTIONS.CLINICAL_LEAVE_EVALUATION]: [
    { clientele: 'Individual', treatment: 'Assessment', partner: 'BlendedCare', offering: 'ClinicalLeave' },
  ],
}

/**
 * Return the best matching CTPO between a selected treatment option and a provider's taxonomy options.
 *
 * Resolution strategy:
 * 1. If preferredCTPO is supplied and the provider supports it, return it (disambiguates when a
 *    treatment option maps to multiple CTPOs, e.g. INDIVIDUAL_THERAPY covers DBT, Fellowship, etc.).
 * 2. Get all candidate CTPOs for the treatment option, ordered by priority.
 * 3. Intersect with the provider's taxonomy options; highest priority match wins.
 * 4. If no match, fall back to provider's first taxonomy, then DEFAULT_PROGRAM_TAXONOMY.
 *
 * With no provider context, return preferredCTPO if it's a candidate for the treatment option,
 * else the highest-priority candidate, else DEFAULT_PROGRAM_TAXONOMY.
 */
export function matchProviderAndTreatmentOptionCTPO(
  treatmentOption: TREATMENT_OPTIONS_TYPE | null,
  providerProgramTaxonomies?: ProviderProgramTaxonomy[],
  preferredCTPO?: ProviderProgramTaxonomy | null,
): ProviderProgramTaxonomy {
  const candidateCTPOs = treatmentOption ? getCandidateCTPOsForTreatmentOption(treatmentOption) : []

  // No provider context → prefer the supplied CTPO if it matches treatment option
  if (!providerProgramTaxonomies || providerProgramTaxonomies.length === 0) {
    if (preferredCTPO && candidateCTPOs.some((c) => ctpoEquals(c, preferredCTPO))) {
      return preferredCTPO
    }
    return candidateCTPOs[0] ?? DEFAULT_PROGRAM_TAXONOMY
  }

  // Prefer the supplied CTPO when the provider supports it
  if (preferredCTPO) {
    const providerMatch = providerProgramTaxonomies.find((pt) => ctpoEquals(pt, preferredCTPO))
    if (providerMatch) {
      return providerMatch as ProviderProgramTaxonomy
    }
  }

  // Match highest priority CTPO that exists in the provider's taxonomies
  if (candidateCTPOs.length > 0) {
    for (const candidate of candidateCTPOs) {
      const match = providerProgramTaxonomies.find((pt) => ctpoEquals(pt, candidate))
      if (match) {
        return match as ProviderProgramTaxonomy
      }
    }
  }

  // Fallback to provider's first taxonomy
  return (providerProgramTaxonomies[0] as ProviderProgramTaxonomy) ?? DEFAULT_PROGRAM_TAXONOMY
}

/**
 * Returns all CTPO tuples associated with a given triage treatment option, ordered by priority.
 *
 * This can include CTPOs across multiple programs! because treatmentOptions are not unique to a single program
 *
 * If you want to get all CTPOs for a specific program ID,
 * get it from the Program model's program_taxonomy field from LyraTherapy.
 *
 * This is the inverse of `getTriageTreatmentOptionFromCTPO`.
 */
export function getCandidateCTPOsForTreatmentOption(
  treatmentOption: TREATMENT_OPTIONS_TYPE | string,
): ProgramTaxonomyType[] {
  return TREATMENT_OPTION_TO_CTPOS[treatmentOption as TREATMENT_OPTIONS_TYPE] ?? []
}

/**
 * Given a CTPO, returns the corresponding treatment option.
 *
 * This is the inverse of `getCandidateCTPOsForTreatmentOption`.
 */
export function getTriageTreatmentOptionFromCTPO(ctpo: ProviderProgramTaxonomy): TREATMENT_OPTIONS_TYPE | undefined {
  for (const [treatmentOption, ctpos] of Object.entries(TREATMENT_OPTION_TO_CTPOS)) {
    if (ctpos?.some((c) => ctpoEquals(c, ctpo))) {
      return treatmentOption as TREATMENT_OPTIONS_TYPE
    }
  }
  return undefined
}

export function isKnownTreatmentCTPO(ctpo?: Partial<ProviderProgramTaxonomy>): ctpo is ProviderProgramTaxonomy {
  return getTriageTreatmentOptionFromCTPO(ctpo as ProviderProgramTaxonomy) !== undefined
}

/**
 * Return a list of treatment options from a list of CTPOs.
 */
export function getListOfTreatmentOptionsFromProviderTaxonomy(
  taxonomy?: ProviderProgramTaxonomy[],
): TREATMENT_OPTIONS_TYPE[] {
  if (!taxonomy) {
    return []
  }

  return taxonomy
    .map((item) => getTriageTreatmentOptionFromCTPO(item))
    .filter((option): option is TREATMENT_OPTIONS_TYPE => option !== undefined)
}

function ctpoEquals(
  a?: ProgramTaxonomyType | ProviderProgramTaxonomy,
  b?: ProgramTaxonomyType | ProviderProgramTaxonomy,
): boolean {
  return (
    !!a &&
    !!b &&
    a.clientele === b.clientele &&
    a.treatment === b.treatment &&
    a.partner === b.partner &&
    a.offering === b.offering
  )
}
